import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import type { PlaceSuggestion } from 'hooks/shared/useGoogleAddressAutocomplete';
import useFeatures from 'hooks/useFeatures';
import { PinIcon } from 'utils/icons/figmaIcons';
import { PlusIcon } from 'utils/icons';

export const getAddressSuggestionOptionId = (
  listboxId: string,
  index: number,
) => `${listboxId}-option-${index}`;

type AddressSearchFieldSuggestionsProps = {
  listboxId: string;
  suggestions: PlaceSuggestion[];
  activeIndex: number;
  onPick: (suggestion: PlaceSuggestion) => void;
  /**
   * When provided, renders a final "Enter manually" row at the bottom of the
   * list (design: blue "Use '{query}' as street name" + gray hint).
   * Omit to hide the row entirely.
   */
  manualEntry?: {
    /** Current search query shown in the row's primary label. */
    query: string;
    onSelect: () => void;
  };
};

const AddressSearchFieldSuggestions: FC<AddressSearchFieldSuggestionsProps> = ({
  listboxId,
  suggestions,
  activeIndex,
  onPick,
  manualEntry,
}) => {
  const { t } = useTranslation();
  const postcodeManualHintKey = useFeatures(
    'address.postcodeManualHintI18nKey',
  ) as string | undefined;

  return (
    <ul
      id={listboxId}
      role="listbox"
      aria-label="Address suggestions"
      onMouseDown={(e) => e.preventDefault()}
      className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-[rgba(27,27,27,0.1)] rounded-[10px] shadow-[0px_1px_3px_0px_rgba(27,27,27,0.05)] max-h-60 overflow-y-auto py-1"
    >
      {suggestions.map((suggestion, index) => (
        <li
          id={getAddressSuggestionOptionId(listboxId, index)}
          key={suggestion.placeId}
          role="option"
          aria-selected={index === activeIndex}
          onMouseDown={(e) => {
            e.preventDefault();
            onPick(suggestion);
          }}
          className="px-1"
        >
          <div
            className={classNames(
              'flex items-start gap-1.5 px-2 py-1.5 rounded-lg cursor-pointer',
              {
                'bg-[#f6f6f6]': index === activeIndex,
                'hover:bg-gray-50': index !== activeIndex,
              },
            )}
          >
            <PinIcon className="w-6 h-6 shrink-0 text-gray-400" />
            <div className="flex flex-col">
              <span className="text-sm font-medium text-gray-900">
                {suggestion.mainText}
              </span>
              {suggestion.secondaryText && (
                <span className="text-sm text-gray-500">
                  {suggestion.secondaryText}
                </span>
              )}
            </div>
          </div>
        </li>
      ))}

      {manualEntry && (
        <li
          id={getAddressSuggestionOptionId(listboxId, suggestions.length)}
          role="option"
          aria-selected={activeIndex === suggestions.length}
          onMouseDown={(e) => {
            e.preventDefault();
            manualEntry.onSelect();
          }}
          className="px-1"
        >
          <div
            className={classNames(
              'flex items-start gap-1.5 px-2 py-1.5 rounded-lg cursor-pointer',
              {
                'bg-[#f6f6f6]': activeIndex === suggestions.length,
                'hover:bg-gray-50': activeIndex !== suggestions.length,
              },
            )}
          >
            <PlusIcon className="w-6 h-6 shrink-0 text-gray-400" />
            <div className="flex flex-col">
              <span className="text-sm font-medium text-bank">
                {t('address.use_as_street_name', { query: manualEntry.query })}
              </span>
              {postcodeManualHintKey ? (
                <span className="text-sm text-gray-500">
                  {t(postcodeManualHintKey)}
                </span>
              ) : null}
            </div>
          </div>
        </li>
      )}
    </ul>
  );
};

export default AddressSearchFieldSuggestions;
